import { FC, useCallback, useMemo, useState } from 'react';
import { formatInTimeZone } from 'date-fns-tz';

import { Slot } from '../api/getAvailability';
import { forRange } from '../utilities/forRange';
import { preventDefault } from '../utilities/preventDefault';
import { ArrowLeft } from './ArrowLeft';
import { ArrowRight } from './ArrowRight';
import { useCalendarContext } from './CalendarProvider';

interface Props {
  day: string | null;
  slots: Slot[];
  onSelectSlot: (slot: Slot) => void;
}

const MAX_PAGE_SIZE = 12;
const PAGE_SIZE = 10;

export const SlotHolster: FC<Props> = ({ day: _day, slots, onSelectSlot }) => {
  const { selectedTimeZone } = useCalendarContext();
  const [paginationIndex, setPaginationIndex] = useState(0);

  const needsPagination = useMemo(() => {
    return slots.length > MAX_PAGE_SIZE;
  }, [slots]);

  const pageCount = useMemo(() => {
    if (slots.length < 13) return 1;
    return Math.ceil(slots.length / PAGE_SIZE);
  }, [slots]);

  const canPrev = needsPagination && paginationIndex > 0;
  const canNext = needsPagination && paginationIndex < pageCount - 1;

  const slotsInView = useMemo(() => {
    if (!needsPagination) return slots;
    const start = paginationIndex * PAGE_SIZE;
    return slots.slice(start, start + PAGE_SIZE);
  }, [needsPagination, paginationIndex, slots]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const prevPagination = useCallback(
    preventDefault(() => {
      if (paginationIndex === 0) return;
      setPaginationIndex(paginationIndex - 1);
    }),
    [paginationIndex],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const nextPagination = useCallback(
    preventDefault(() => {
      if (paginationIndex === pageCount - 1) return;
      setPaginationIndex(paginationIndex + 1);
    }),
    [slots, paginationIndex],
  );

  return (
    <div className="week slot-wrapper">
      <div className="slot-holster">
        {needsPagination && (
          <button
            className={`slot pagination ${canPrev ? '' : 'disabled'}`}
            onClick={prevPagination}
          >
            <ArrowLeft />
            &nbsp;Previous
          </button>
        )}
        {slotsInView.map(slot => (
          <button
            className="slot"
            key={slot.startTime.toString()}
            onClick={() => onSelectSlot(slot)}
          >
            {formatInTimeZone(
              new Date(slot.startTime),
              selectedTimeZone,
              'h:mmaaa',
            )}
          </button>
        ))}
        {needsPagination && (
          <button
            className={`slot pagination ${canNext ? '' : 'disabled'}`}
            onClick={nextPagination}
          >
            Next&nbsp;
            <ArrowRight />
          </button>
        )}
      </div>
      {needsPagination && (
        <div className="pagination-stepper">
          {forRange(0, pageCount, i => (
            <div
              key={i}
              className={`step ${i === paginationIndex ? 'active' : ''}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
